import { useEffect, useRef, useState } from 'react';

export const useStereoAudio = ({ src }: { onStart?: () => void; src: string }) => {
  const [initialized, setInitialized] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(undefined);
  const audioCtxRef = useRef<AudioContext>(undefined);
  const leftGainRef = useRef<GainNode>(undefined);
  const rightGainRef = useRef<GainNode>(undefined);

  useEffect(() => {
    const audioCtx = new AudioContext();
    const audio = new Audio(src);
    audio.loop = true; // Loop the audio

    const source = audioCtx.createMediaElementSource(audio);

    // Split the stereo channels
    const splitter = audioCtx.createChannelSplitter(2);

    // Create gain nodes for each channel
    const leftGain = audioCtx.createGain();
    const rightGain = audioCtx.createGain();

    // Merge channels back together
    const merger = audioCtx.createChannelMerger(2);

    // Connect graph: source -> splitter
    source.connect(splitter);

    // splitter left channel -> left gain -> merger left input
    splitter.connect(leftGain, 0);
    leftGain.connect(merger, 0, 0);

    // splitter right channel -> right gain -> merger right input
    splitter.connect(rightGain, 1);
    rightGain.connect(merger, 0, 1);

    // merger -> destination
    merger.connect(audioCtx.destination);

    // Save references for later control
    audioRef.current = audio;
    audioCtxRef.current = audioCtx;
    leftGainRef.current = leftGain;
    rightGainRef.current = rightGain;

    leftGain.gain.value = 1; // Full volume for left channel
    rightGain.gain.value = 1; // Full volume for right channel

    setInitialized(true);

    return () => {
      // Clean up audio context and nodes
      if (audioCtx.state !== 'closed') {
        audioCtx.close();
      }
      audioRef.current = undefined;
      leftGainRef.current = undefined;
      rightGainRef.current = undefined;
    };
  }, []);

  const togglePlay = async () => {
    if (audioCtxRef.current?.state === 'suspended') {
      await audioCtxRef.current.resume();
    }

    if (audioRef.current) {
      if (!isPlaying) {
        await audioRef.current.play();
      } else {
        await audioRef.current.pause();
      }

      setIsPlaying((prev) => !prev);
    }
  };

  const setLeftGain = (value: number) => {
    if (leftGainRef.current) {
      leftGainRef.current.gain.value = value;
    }
  };

  const setRightGain = (value: number) => {
    if (rightGainRef.current) {
      rightGainRef.current.gain.value = value;
    }
  };

  const pause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  return {
    initialized,
    togglePlay,
    isPlaying,
    pause,
    setLeftGain,
    setRightGain,
  };
};
